import { ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons"
import * as Select from "@radix-ui/react-select"
import React, { ComponentProps, useMemo } from "react"
import { twMerge } from "tailwind-merge"
import { tv } from "tailwind-variants"

export type SelectVariantType = "charcoal" | "glass" | "outline" | "reversed"

type Props = ComponentProps<typeof Select.Root> & {
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  options: { label: string; value: string | undefined }[]
  size?: "default" | "rounded" | "chip"
  required?: boolean
  disabled?: boolean
  error?: string
  variant?: SelectVariantType
  className?: string
  active?: boolean
  allowNull?: boolean
}

/* fix for not fully working with typescript 5 and react 18 ... */
/* https://github.com/radix-ui/primitives/issues/2309 */
type SelectPortalProps = ComponentProps<typeof Select.Portal> & {
  position?: "popper" | "anchor"
  sideOffset?: number
}
const SelectPortal = Select.Portal as React.FC<SelectPortalProps>

const triggerVariants = tv({
  base: "flex items-center justify-between transition-colors duration-200 outline-none border-solid error:text-error-dark error:border-error-dark error:bg-error-light error:data-[placeholder]:text-error-dark error:hover:bg-grey-light field-stretch",
  variants: {
    variant: {
      default:
        "bg-white text-black border-b border-black hover:bg-grey-light data-[placeholder]:text-grey-dark data-[disabled]:border-grey-light data-[disabled]:text-grey-light data-[disabled]:bg-white data-[disabled]:data-[placeholder]:text-grey-light",
      charcoal:
        "bg-black text-white border border-black hover:bg-grey-dark hover:border-grey-dark hover:text-white data-[disabled]:bg-grey-dark data-[disabled]:border-grey-dark data-[disabled]:text-grey-mid",
      glass:
        "bg-glass text-black border border-glass hover:bg-grey-light hover:border-grey-light hover:text-black data-[disabled]:bg-grey-light data-[disabled]:border-grey-light data-[disabled]:text-white",
      outline:
        "bg-glass text-black border-[0.5px] border-black hover:bg-grey-light hover:border-black hover:text-black data-[disabled]:bg-glass data-[disabled]:border-grey-light data-[disabled]:text-grey-light",
      reversed:
        "bg-black text-white border border-black hover:bg-glass hover:border-glass hover:text-black data-[disabled]:bg-black data-[disabled]:border-black data-[disabled]:text-grey-dark",
    },
    size: {
      default: "h-button min-w-selectMinW px-2 py-0 type-ui-m-light",
      rounded: "h-buttonLg px-4 min-w-selectMinW rounded-lg data-[state=open]:rounded-bl-none data-[state=open]:rounded-br-none data-[state=open]:border-b-0 type-ui-l-light",
      chip: "rounded-md data-[state=open]:rounded-bl-none data-[state=open]:rounded-br-none data-[state=open]:border-b-0 w-fit min-w-selectChipMinW p-2 h-buttonChip type-ui-s-roman-caps [&_svg]:w-buttonChipIcon [&_svg]:h-buttonChipIcon",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

const portalContentVariants = tv({
  base: "overflow-hidden transition-colors duration-200 w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)]",
  variants: {
    variant: {
      default: "",
      charcoal: "bg-black",
      glass: "bg-glass",
      outline: "bg-glass data-[state=open]:border-[0.5px] data-[state=open]:border-solid data-[state=open]:border-current data-[state=open]:border-t-0",
      reversed: "bg-black",
    },
    size: {
      default: "bg-white min-w-selectMinW",
      rounded: "rounded-bl-lg rounded-br-lg",
      chip: "rounded-bl-md rounded-br-md pb-1",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

const selectItemVariants = tv({
  base: "flex items-center outline-none w-full transition-colors duration-200 cursor-pointer",
  variants: {
    variant: {
      default:
        "bg-white text-black data-[disabled]:border-grey-light data-[disabled]:text-grey-light data-[disabled]:bg-white data-[highlighted]:bg-grey-light data-[highlighted]:text-black",
      charcoal:
        "bg-black text-white border-black data-[highlighted]:bg-grey-dark data-[highlighted]:border-grey-dark data-[highlighted]:text-white data-[disabled]:bg-grey-dark data-[disabled]:border-grey-dark data-[disabled]:text-grey-mid",
      glass:
        "bg-glass text-black border-glass data-[highlighted]:bg-grey-light data-[highlighted]:border-grey-light data-[highlighted]:text-black data-[disabled]:bg-grey-light data-[disabled]:border-grey-light data-[disabled]:text-white",
      outline:
        "bg-glass text-black border-black data-[highlighted]:bg-black data-[highlighted]:border-black data-[highlighted]:text-white data-[disabled]:bg-glass data-[disabled]:border-grey-light data-[disabled]:text-grey-light",
      reversed:
        "bg-black text-white border-black data-[highlighted]:bg-glass data-[highlighted]:border-glass data-[highlighted]:text-black data-[disabled]:bg-black data-[disabled]:border-black data-[disabled]:text-grey-dark",
    },
    size: {
      default: "px-2 py-2 type-ui-m-light",
      rounded: "px-4 py-4 type-ui-l-light",
      chip: "p-2 h-buttonChip type-ui-s-roman-caps",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

export function RichSelect(props: Props) {
  const { placeholder, value, onChange, options, size, required, disabled, error, variant, className, allowNull, ...additionalProps } = props

  return (
    <Select.Root {...additionalProps} disabled={disabled} required={required} onValueChange={onChange} defaultValue={value} value={value}>
      <Select.Trigger className={twMerge(triggerVariants({ variant, size }), className)} aria-label={placeholder}>
        <Select.Value placeholder={placeholder || "Select"} />
        <Select.Icon className="pl-1 text-current">
          <ChevronDownIcon />
        </Select.Icon>
      </Select.Trigger>

      <SelectPortal position="popper" sideOffset={0}>
        <Select.Content className={portalContentVariants({ variant, size })}>
          <Select.ScrollUpButton className="flex items-center justify-center py-1 bg-white text-current cursor-default">
            <ChevronUpIcon />
          </Select.ScrollUpButton>
          <Select.Viewport>
            <Select.Group>
              {useMemo(() => {
                return options
                  .filter((option) => {
                    if (!option.value) {
                      if (props.value) return true
                      return false
                    }
                    return true
                  })
                  .map((option, i) => (
                    <Select.Item key={i} className={twMerge(selectItemVariants({ variant, size }), className)} value={option.value!}>
                      <Select.ItemText>{option.label}</Select.ItemText>
                    </Select.Item>
                  ))
              }, [options, props.value])}
            </Select.Group>
          </Select.Viewport>
          <Select.ScrollDownButton className="flex items-center justify-center py-1 bg-white text-current cursor-default">
            <ChevronDownIcon />
          </Select.ScrollDownButton>
        </Select.Content>
      </SelectPortal>
    </Select.Root>
  )
}
